import { cn } from "@/lib/utils";
import { MAX_COLS, MAX_ROWS, type LayoutMode } from "@/lib/dashboardLayout";

// Compact dashboard layout control: a 3-way segmented toggle (Auto / Grid /
// Freeform) plus inline cols/rows steppers shown only while Grid is active.
// Rendered above the tile grid by DashboardTiles.

const MODES: { mode: LayoutMode; label: string; glyph: string; title: string }[] = [
  { mode: "auto", label: "Auto", glyph: "⊞", title: "Responsive grid, drag to reorder" },
  { mode: "grid", label: "Grid", glyph: "▦", title: "Fixed columns × rows; place tiles in cells" },
  { mode: "freeform", label: "Freeform", glyph: "⤢", title: "Position tiles freely on a canvas" },
];

function Stepper({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  const set = (v: number) => onChange(Math.min(max, Math.max(min, v)));
  return (
    <div className="flex items-center gap-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <div className="flex items-center rounded-md border border-input">
        <button
          type="button"
          className="h-7 w-7 text-sm leading-none hover:bg-accent disabled:opacity-40"
          disabled={value <= min}
          onClick={() => set(value - 1)}
          aria-label={`Decrease ${label}`}
        >
          −
        </button>
        <span className="w-6 text-center tabular-nums">{value}</span>
        <button
          type="button"
          className="h-7 w-7 text-sm leading-none hover:bg-accent disabled:opacity-40"
          disabled={value >= max}
          onClick={() => set(value + 1)}
          aria-label={`Increase ${label}`}
        >
          +
        </button>
      </div>
    </div>
  );
}

export function LayoutChooser({
  mode,
  cols,
  rows,
  narrow,
  onModeChange,
  onColsChange,
  onRowsChange,
}: {
  mode: LayoutMode;
  cols: number;
  rows: number;
  /** true on phone-width viewports — grid/freeform fall back to a stack */
  narrow: boolean;
  onModeChange: (m: LayoutMode) => void;
  onColsChange: (v: number) => void;
  onRowsChange: (v: number) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex rounded-md border border-input text-xs">
        {MODES.map((m, i) => (
          <button
            key={m.mode}
            type="button"
            title={m.title}
            onClick={() => onModeChange(m.mode)}
            className={cn(
              "flex items-center gap-1.5 px-2.5 h-7 transition-colors",
              i > 0 && "border-l border-input",
              mode === m.mode
                ? "bg-primary text-primary-foreground"
                : "hover:bg-accent",
            )}
          >
            <span aria-hidden className="text-sm leading-none">
              {m.glyph}
            </span>
            {m.label}
          </button>
        ))}
      </div>

      {mode === "grid" && (
        <div className="flex items-center gap-3">
          <Stepper
            label="Cols"
            value={cols}
            min={1}
            max={MAX_COLS}
            onChange={onColsChange}
          />
          <Stepper
            label="Rows"
            value={rows}
            min={1}
            max={MAX_ROWS}
            onChange={onRowsChange}
          />
        </div>
      )}

      {narrow && mode !== "auto" && (
        <span className="text-xs text-muted-foreground">
          Grid layouts apply on wider screens
        </span>
      )}
    </div>
  );
}
